import { 
  ChatBubbleLeftRightIcon, 
  CpuChipIcon, 
  CommandLineIcon,
  RocketLaunchIcon,
  ArrowsPointingOutIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

export const servicesData = [
  {
    slug: 'ai-appointment-setters',
    title: 'AI Agents',
    description: 'Intelligent agents that handle task automation for service businesses. Try our AI Appointment Setters for 24/7 booking capabilities.',
    icon: ChatBubbleLeftRightIcon,
    category: "Business Automation",
    features: [
      "24/7 capabilities without human intervention",
      "Natural language conversations with advanced AI understanding",
      "Seamless integration with popular calendar and CRM systems",
      "Custom booking rules tailored to your business policies",
      "Multi-channel support including web, voice and messaging platforms",
      "Automatic reminder and follow-up communications"
    ],
    benefits: [
      "Reduce staffing costs whilst improving customer service",
      "Never miss a booking opportunity with round-the-clock availability",
      "Increase conversion rates with immediate customer engagement",
      "Free up staff time for high-value activities",
      "Eliminate double-bookings and scheduling errors",
      "Create consistent booking experiences across all channels"
    ],
    useCases: [
      {
        title: "Automotive Dealerships",
        description: "Handle test drive bookings, service appointments and sales enquiries automatically with intelligent AI agents that understand your inventory and availability."
      },
      {
        title: "Professional Services",
        description: "Allow clients to book consultations efficiently whilst ensuring proper qualification and preparation information is collected before meetings."
      },
      {
        title: "Healthcare Providers",
        description: "Streamline patient appointment scheduling with intelligent triage and proper information collection before appointments, improving patient care."
      }
    ],
    faqs: [
      {
        question: "How does the AI Appointment Setter integrate with our existing systems?",
        answer: "Our AI Appointment Setters integrate seamlessly with popular calendar systems including Google Calendar, Outlook, and industry-specific booking platforms. We offer custom API integrations with your existing CRM or booking software, ensuring a smooth transition with minimal disruption to your operations."
      },
      {
        question: "Can the AI be customised to match our brand voice?",
        answer: "Absolutely. We configure each AI Appointment Setter to reflect your organisation's tone, terminology, and communication style. This personalisation ensures customers experience consistent branding throughout their interaction with your business."
      },
      {
        question: "What happens if the AI cannot handle a complex enquiry?",
        answer: "Our AI systems are designed to recognise when a query exceeds their capabilities. In these cases, they can gracefully escalate to a human team member, schedule a callback, or collect information for follow-up, ensuring no customer enquiry goes unaddressed."
      },
      {
        question: "How secure is the customer data collected by the AI?",
        answer: "Security is paramount. All our AI systems are built with enterprise-grade security protocols, including end-to-end encryption, GDPR compliance, and secure data storage. Customer information is processed in accordance with the strictest UK and EU data protection regulations."
      }
    ],
    implementationSteps: [
      {
        number: "01",
        title: "Requirements Analysis",
        description: "We analyse your booking processes, calendar systems, and specific business rules to understand your exact needs."
      },
      {
        number: "02",
        title: "Knowledge Base Creation",
        description: "Our team develops a comprehensive knowledge base about your services, availability rules, and booking policies."
      },
      {
        number: "03",
        title: "AI Configuration & Training",
        description: "We configure and train the AI with your specific services, availability patterns, and communication style."
      },
      {
        number: "04",
        title: "System Integration",
        description: "Seamless integration with your existing calendar, CRM, and business management systems."
      },
      {
        number: "05",
        title: "Testing & Refinement",
        description: "Rigorous testing across various booking scenarios to ensure accuracy and natural conversation flow."
      },
      {
        number: "06",
        title: "Deployment & Ongoing Support",
        description: "Full deployment across your chosen channels with continuous monitoring and improvement."
      }
    ],
    industryStats: [
      {
        value: "35%",
        description: "increase in appointment bookings when businesses implement 24/7 AI booking systems"
      },
      {
        value: "£23k",
        description: "average annual savings in administrative costs for small businesses using AI appointment setters"
      },
      {
        value: "68%",
        description: "of customers prefer self-service booking options outside of normal business hours"
      },
      {
        value: "41%",
        description: "reduction in no-shows from automated reminders and confirmation processes"
      }
    ],
    potentialOutcomes: {
      title: "What We Can Do For Your Business",
      description: "Our AI Appointment Setters can transform your booking processes, delivering measurable improvements across multiple business metrics:",
      outcomes: [
        "Increase appointment bookings by 30-45% through 24/7 availability",
        "Reduce administrative staff costs by up to 25%",
        "Improve customer satisfaction through immediate response and efficient booking",
        "Eliminate scheduling errors and double-bookings",
        "Gather valuable customer insights through interaction analysis"
      ]
    }
  },
  
  {
    slug: 'custom-ai-solutions',
    title: 'Custom AI Solutions',
    description: 'Tailored machine learning models and AI applications built for your specific business needs, creating unique competitive advantages through proprietary capabilities.',
    icon: CpuChipIcon,
    category: "Bespoke Development",
    features: [
      "Proprietary machine learning models built specifically for your use case",
      "Integration with existing business systems and data sources",
      "Continuous improvement through retraining and refinement",
      "Comprehensive data privacy and security measures",
      "Customised dashboards and reporting interfaces",
      "On-premises or cloud-based deployment options"
    ],
    benefits: [
      "Solve unique business challenges that off-the-shelf solutions cannot address",
      "Create competitive advantages through proprietary AI capabilities",
      "Maximise ROI by focusing AI development on high-value business areas",
      "Full ownership of AI solutions tailored to your specific requirements",
      "Adapt quickly to changing market conditions with flexible AI systems",
      "Improve decision-making with insights specific to your business context"
    ],
    useCases: [
      {
        title: "Predictive Maintenance",
        description: "Custom AI models that predict equipment failures before they happen, reducing downtime and maintenance costs in manufacturing environments."
      },
      {
        title: "Financial Risk Assessment",
        description: "Bespoke machine learning systems that analyse complex datasets to identify risks and opportunities specific to your financial services business."
      },
      {
        title: "Supply Chain Optimisation",
        description: "AI solutions that adapt to your unique supply chain challenges, improving efficiency and reducing costs through intelligent forecasting and logistics planning."
      }
    ],
    faqs: [
      {
        question: "What kind of data do we need to develop a custom AI solution?",
        answer: "The data requirements depend on your specific use case. Generally, we work with historical business data, operational metrics, customer interactions, or domain-specific datasets. Our team conducts a comprehensive data assessment to determine what's needed and can advise on data collection strategies if required."
      },
      {
        question: "How long does it take to develop a custom AI solution?",
        answer: "Development timelines typically range from 2-6 months, depending on the complexity of the solution and data readiness. We follow an agile development methodology with regular milestones to ensure visibility throughout the process. Our approach allows for early prototype testing and continuous refinement."
      },
      {
        question: "Who owns the intellectual property of the custom AI solution?",
        answer: "Our clients retain full ownership of the custom AI solutions we develop, including all associated models, code, and intellectual property. This ensures you maintain complete control over your competitive advantages and can continue to evolve the solution as your business needs change."
      },
      {
        question: "How do you ensure our proprietary data remains secure?",
        answer: "We implement strict security protocols throughout the development process, including data encryption, secure development environments, and comprehensive access controls. All work is conducted under strict confidentiality agreements, and we can work within your existing security frameworks for particularly sensitive applications."
      }
    ],
    implementationSteps: [
      {
        number: "01",
        title: "Problem Definition & Scoping",
        description: "We work closely with your team to define the specific business challenge and desired outcomes for the AI solution."
      },
      {
        number: "02",
        title: "Data Assessment & Preparation",
        description: "Our data scientists evaluate your available data sources and prepare them for model development."
      },
      {
        number: "03",
        title: "Solution Architecture",
        description: "We design a comprehensive architecture for your custom AI solution, including all components and integrations."
      },
      {
        number: "04",
        title: "Model Development & Training",
        description: "Our engineers develop and train the machine learning models using your data and business rules."
      },
      {
        number: "05",
        title: "Interface Development",
        description: "We create intuitive interfaces and integration points for users to interact with the AI system."
      },
      {
        number: "06",
        title: "Testing & Deployment",
        description: "Comprehensive testing in your business environment followed by full deployment and knowledge transfer."
      }
    ],
    industryStats: [
      {
        value: "83%",
        description: "of businesses report that custom AI solutions outperform off-the-shelf alternatives"
      },
      {
        value: "£2.4M",
        description: "average annual value created by custom AI solutions for enterprise businesses"
      },
      {
        value: "57%",
        description: "of executives cite custom AI capabilities as creating significant competitive advantage"
      },
      {
        value: "4.2x",
        description: "higher ROI from tailored AI solutions compared to generic implementations"
      }
    ],
    potentialOutcomes: {
      title: "What We Can Do For Your Business",
      description: "Our Custom AI Solutions can deliver transformative capabilities specific to your business challenges:",
      outcomes: [
        "Develop proprietary AI models that create unique competitive advantages",
        "Automate complex decision-making processes with high accuracy",
        "Extract actionable insights from your existing data assets",
        "Reduce operational costs through intelligent process optimisation",
        "Create new revenue streams through AI-enabled products or services"
      ]
    }
  },
  
  {
    slug: 'rag-bots',
    title: 'RAG Bots',
    description: 'Retrieval-Augmented Generation bots that provide accurate, context-aware responses based on your company knowledge base, documentation, and proprietary information.',
    icon: CommandLineIcon,
    category: "Intelligent Assistants",
    features: [
      "Integration with your internal knowledge repositories and documentation",
      "Accurate responses grounded in your company's specific information",
      "Conversational AI that understands complex queries and context",
      "Multilingual support for global operations",
      "Role-based access controls for sensitive information",
      "Continuous learning from interactions and feedback"
    ],
    benefits: [
      "Deliver instant, accurate answers to customer and employee questions",
      "Ensure consistent information across all communications channels",
      "Reduce support ticket volume by 30-50% through self-service",
      "Scale knowledge distribution without increasing staff",
      "Preserve and activate institutional knowledge",
      "Improve customer and employee satisfaction through quick resolution"
    ],
    useCases: [
      {
        title: "Internal Knowledge Management",
        description: "Empower employees with instant access to company policies, procedures and institutional knowledge through natural language queries."
      },
      {
        title: "Customer Support Automation",
        description: "Provide instant, accurate responses to customer inquiries based on your product documentation, FAQs, and support knowledge base."
      },
      {
        title: "Technical Documentation Assistant",
        description: "Help developers, engineers, and technical staff quickly find and utilize information from complex technical documentation."
      }
    ],
    faqs: [
      {
        question: "What types of information can RAG Bots work with?",
        answer: "Our RAG Bots can process and understand a wide range of information sources including policy documents, product manuals, internal wikis, knowledge bases, technical documentation, FAQs, training materials, and more. They work with text-based content in various formats including PDF, Word, HTML, and Markdown."
      },
      {
        question: "How accurate are the responses from RAG Bots?",
        answer: "RAG technology significantly improves accuracy compared to traditional AI by grounding responses in your verified information. Our systems achieve 92-96% accuracy in information retrieval and response generation when properly configured with quality source material."
      },
      {
        question: "How do you handle sensitive or confidential information?",
        answer: "Our RAG Bots implement robust security measures including role-based access controls, information classification systems, and content filtering mechanisms. This ensures that users only receive information they're authorized to access, maintaining compliance with your security policies."
      },
      {
        question: "Do RAG Bots learn and improve over time?",
        answer: "Yes, our RAG Bots feature continuous learning capabilities. They improve through user feedback, interaction analysis, and regular knowledge base updates. As your information evolves, the system adapts to provide increasingly relevant and accurate responses."
      }
    ],
    implementationSteps: [
      {
        number: "01",
        title: "Knowledge Assessment",
        description: "We evaluate your existing information sources and knowledge repositories to determine suitability and preparation needs."
      },
      {
        number: "02",
        title: "Content Processing & Indexing",
        description: "Your information is processed, structured, and indexed to optimize retrieval accuracy and performance."
      },
      {
        number: "03",
        title: "RAG Model Configuration",
        description: "We configure the retrieval and generation components to work effectively with your specific knowledge domain."
      },
      {
        number: "04",
        title: "Interface Development",
        description: "User interfaces are created for your specific use cases, whether internal tools or customer-facing applications."
      },
      {
        number: "05",
        title: "Testing & Tuning",
        description: "Comprehensive testing with domain experts to ensure accuracy, relevance, and proper handling of edge cases."
      },
      {
        number: "06",
        title: "Deployment & Monitoring",
        description: "Full deployment with ongoing monitoring, analytics, and continuous improvement processes."
      }
    ],
    industryStats: [
      {
        value: "47%",
        description: "reduction in support tickets when RAG-based systems are implemented"
      },
      {
        value: "78%",
        description: "of employees report increased productivity with access to RAG knowledge assistants"
      },
      {
        value: "92%",
        description: "average response accuracy using RAG technology compared to 63% with traditional chatbots"
      },
      {
        value: "3.4x",
        description: "faster information retrieval compared to conventional search methods"
      }
    ],
    potentialOutcomes: {
      title: "What We Can Do For Your Business",
      description: "Our RAG Bots can transform how your organisation manages and leverages knowledge:",
      outcomes: [
        "Create a unified access point for all organisational knowledge",
        "Reduce support costs while improving response quality and speed",
        "Enable staff to make better-informed decisions based on complete information",
        "Preserve institutional knowledge when key employees depart",
        "Scale support operations without proportional staff increases"
      ]
    }
  },
  
  {
    slug: 'ai-web-applications',
    title: 'AI Web Applications',
    description: 'Full-stack web applications powered by cutting-edge LLMs and AI technologies that deliver intelligent, adaptive user experiences for your customers and employees.',
    icon: RocketLaunchIcon,
    category: "Software Development",
    features: [
      "Custom web applications with embedded AI capabilities",
      "Intelligent user interfaces that adapt to user behavior",
      "Integration with leading AI models and APIs",
      "Responsive design for all devices and screen sizes",
      "Secure authentication and data handling",
      "Analytics dashboards to track usage and performance"
    ],
    benefits: [
      "Deliver unique, AI-enhanced experiences to your customers",
      "Automate complex workflows through intelligent web interfaces",
      "Reduce development costs compared to traditional approaches",
      "Create scalable solutions that grow with your business",
      "Generate valuable user insights through AI interaction analysis",
      "Rapidly deploy AI capabilities without extensive infrastructure"
    ],
    useCases: [
      {
        title: "GolfAI Guru",
        description: "An intelligent golf coach web application that uses computer vision to analyze swing techniques, extracts pose data, and provides personalized coaching feedback through an LLM trained on professional swings."
      },
      {
        title: "Intelligent Customer Portals",
        description: "Create personalised customer portals that learn from user behavior and provide predictive assistance and recommendations."
      },
      {
        title: "Internal Productivity Tools",
        description: "Develop AI-powered workspaces that streamline complex business processes and increase employee productivity."
      },
      {
        title: "AI-Enhanced E-commerce",
        description: "Build shopping experiences with intelligent product recommendations, virtual assistants, and personalised user journeys."
      }
    ],
    faqs: [
      {
        question: "How long does it take to develop a custom AI web application?",
        answer: "Development timelines typically range from 8-16 weeks for initial launches, depending on complexity and scope. We follow an agile methodology with regular deliverables and can often deploy minimum viable products (MVPs) within 4-6 weeks to gather early feedback."
      },
      {
        question: "Can AI web applications integrate with our existing systems?",
        answer: "Yes, our applications are designed for seamless integration with your existing infrastructure. We can connect to your CRM, ERP, inventory systems, databases, and other business tools through APIs, webhooks, or custom connectors to ensure data flows smoothly across your organisation."
      },
      {
        question: "How do you ensure the security of AI web applications?",
        answer: "Security is built into every layer of our development process. We implement industry best practices including encryption of sensitive data, secure authentication protocols, regular security audits, vulnerability testing, and compliance with relevant standards like GDPR, HIPAA, or PCI DSS depending on your industry requirements."
      },
      {
        question: "How are these applications maintained and updated?",
        answer: "We offer comprehensive maintenance packages that include regular updates, performance monitoring, security patches, and ongoing optimisation. As AI technologies evolve rapidly, we also provide roadmaps for incorporating new capabilities to keep your application at the cutting edge."
      }
    ],
    implementationSteps: [
      {
        number: "01",
        title: "Requirements & Discovery",
        description: "We work with your team to understand your business objectives and define the application requirements."
      },
      {
        number: "02",
        title: "UX/UI Design",
        description: "Our designers create intuitive, intelligent interfaces optimized for your specific use cases."
      },
      {
        number: "03",
        title: "AI Integration Planning",
        description: "We design the architecture for AI components and how they'll integrate with the application."
      },
      {
        number: "04",
        title: "Development & Testing",
        description: "Our development team builds the application with continuous testing throughout the process."
      },
      {
        number: "05",
        title: "User Acceptance & Training",
        description: "We conduct thorough testing with actual users and provide training for your team."
      },
      {
        number: "06",
        title: "Deployment & Ongoing Support",
        description: "Full deployment to production with continued monitoring, maintenance, and feature enhancements."
      }
    ],
    industryStats: [
      {
        value: "67%",
        description: "of users report higher satisfaction with AI-enhanced web experiences"
      },
      {
        value: "43%",
        description: "increase in conversion rates for websites with intelligent personalization"
      },
      {
        value: "72%",
        description: "of businesses report competitive advantage from AI-powered web applications"
      },
      {
        value: "31%",
        description: "reduction in support costs through intelligent self-service capabilities"
      }
    ],
    potentialOutcomes: {
      title: "What We Can Do For Your Business",
      description: "Our AI Web Applications can create transformative digital experiences:",
      outcomes: [
        "Create intuitive interfaces that anticipate user needs before they're expressed",
        "Automate complex workflows that previously required manual handling",
        "Deliver personalised experiences that adapt to each user's preferences",
        "Generate actionable business insights from user interaction patterns",
        "Enable new digital service offerings previously not possible"
      ]
    }
  },
  
  {
    slug: 'process-automation',
    title: 'Process Automation',
    description: 'Streamline your business operations with intelligent automation solutions that eliminate repetitive tasks, reduce errors, and free your team to focus on high-value activities.',
    icon: ArrowsPointingOutIcon,
    category: "Operational Efficiency",
    features: [
      "Intelligent workflow automation with decision-making capabilities",
      "Document processing and data extraction from unstructured content",
      "Integration with existing business systems and databases",
      "Visual process designers for non-technical users",
      "Real-time monitoring and performance analytics",
      "Exception handling and human-in-the-loop workflows"
    ],
    benefits: [
      "Reduce operational costs through elimination of manual tasks",
      "Improve accuracy and consistency across business processes",
      "Accelerate processing times for critical business operations",
      "Scale operations without proportional increases in headcount",
      "Free employees from repetitive tasks for higher-value work",
      "Create audit trails and improve compliance documentation"
    ],
    useCases: [
      {
        title: "Intelligent Document Processing",
        description: "Automatically extract, validate and process information from invoices, contracts, applications and other business documents."
      },
      {
        title: "Approval Workflow Automation",
        description: "Streamline multi-stage approval processes with intelligent routing, reminders, and exception handling for various business functions."
      },
      {
        title: "Data Integration & Synchronisation",
        description: "Eliminate manual data entry across systems with automated data flows that maintain consistency across your business applications."
      }
    ],
    faqs: [
      {
        question: "Which processes are best suited for automation?",
        answer: "The best candidates for automation are typically processes that are repetitive, rule-based, high-volume, and error-prone. These include data entry and validation, document processing, approval workflows, report generation, system integrations, and compliance checks. Our team conducts process assessments to identify your highest-value automation opportunities."
      },
      {
        question: "How disruptive is implementing process automation?",
        answer: "We design implementations to minimise business disruption. Our approach typically involves parallel running of automated and existing processes initially, with phased cutover once stability is confirmed. Most automation projects can be implemented with minimal impact on day-to-day operations and without significant downtime."
      },
      {
        question: "Do we need to replace our existing systems to implement automation?",
        answer: "No. Our process automation solutions are designed to work with your existing systems rather than replace them. We use APIs, RPA technologies, webhooks, and other integration methods to connect with your current applications, extending their capabilities rather than requiring replacement."
      },
      {
        question: "How do you handle process exceptions and edge cases?",
        answer: "Our automation platforms include sophisticated exception handling capabilities. When unusual situations arise, the system can route cases to appropriate human reviewers, provide relevant context and information, and learn from the resolution to improve future handling. This ensures smooth operations even when non-standard scenarios occur."
      }
    ],
    implementationSteps: [
      {
        number: "01",
        title: "Process Assessment & Selection",
        description: "We analyse your operations to identify high-value automation opportunities and prioritize implementations."
      },
      {
        number: "02",
        title: "Process Mapping & Optimization",
        description: "Current processes are documented and optimized before automation to avoid simply digitizing inefficient workflows."
      },
      {
        number: "03",
        title: "Solution Architecture",
        description: "We design the automation solution, integration points, and technology components required."
      },
      {
        number: "04",
        title: "Development & Configuration",
        description: "The automation solution is built, configured and integrated with your systems."
      },
      {
        number: "05",
        title: "Testing & Validation",
        description: "Comprehensive testing ensures the automated process handles all scenarios correctly."
      },
      {
        number: "06",
        title: "Deployment & Monitoring",
        description: "We deploy the solution with monitoring tools and provide training for your team."
      }
    ],
    industryStats: [
      {
        value: "40%",
        description: "average cost reduction for processes transformed through intelligent automation"
      },
      {
        value: "66%",
        description: "faster processing times for automated workflows compared to manual handling"
      },
      {
        value: "92%",
        description: "reduction in error rates through elimination of manual data entry"
      },
      {
        value: "3-5x",
        description: "return on investment within the first year of implementation"
      }
    ],
    potentialOutcomes: {
      title: "What We Can Do For Your Business",
      description: "Our Process Automation solutions can transform your operational efficiency:",
      outcomes: [
        "Reduce processing times from days to minutes for key business operations",
        "Eliminate data entry errors and inconsistencies across systems",
        "Enable 24/7 processing for critical business workflows",
        "Create capacity for growth without proportional staffing increases",
        "Improve compliance with automatic audit trails and documentation"
      ]
    }
  },
  
  {
    slug: 'data-science-solutions',
    title: 'Data Science Solutions',
    description: 'Advanced analytics and data-driven insights to power your business decisions, uncover hidden patterns, and predict future outcomes with sophisticated machine learning models.',
    icon: ChartBarIcon,
    category: "Analytics & Insights",
    features: [
      "Predictive analytics and forecasting models",
      "Customer segmentation and behaviour analysis",
      "Anomaly detection and risk identification",
      "Natural language processing for unstructured data",
      "Interactive dashboards and visualisations",
      "Machine learning model development and deployment"
    ],
    benefits: [
      "Make data-driven decisions based on robust statistical analysis",
      "Identify growth opportunities hidden in your existing data",
      "Predict future trends and outcomes with statistical confidence",
      "Understand customer behaviour patterns at granular levels",
      "Optimise operations through data-driven insights",
      "Quantify and mitigate business risks proactively"
    ],
    useCases: [
      {
        title: "Predictive Sales Forecasting",
        description: "Develop accurate sales predictions incorporating multiple variables including seasonality, marketing activities, economic indicators, and historical patterns."
      },
      {
        title: "Customer Segmentation & Analysis",
        description: "Identify high-value customer segments and understand behaviour patterns to improve targeting, retention and lifetime value."
      },
      {
        title: "Operational Optimisation",
        description: "Improve efficiency through data-driven insights that identify bottlenecks, predict resource needs, and optimise complex operational systems."
      }
    ],
    faqs: [
      {
        question: "What kind of data is required for effective data science projects?",
        answer: "While more data generally produces better results, the quality and relevance of data are equally important. We can work with various data types including transaction records, customer information, operational metrics, time-series data, text, and in some cases, even limited datasets. Our team will assess your available data and advise on collection strategies if needed."
      },
      {
        question: "How do you ensure the accuracy and reliability of predictive models?",
        answer: "We employ rigorous validation methodologies including cross-validation, backtesting, and out-of-sample testing. Our models include confidence intervals and prediction bands to quantify uncertainty. We also implement ongoing monitoring systems to detect model drift and ensure continued accuracy as conditions change."
      },
      {
        question: "How do you make complex data science results accessible to non-technical stakeholders?",
        answer: "We create intuitive visualisations and interactive dashboards tailored to different user needs. For executives, we provide high-level insights with clear business implications. For operational teams, we develop actionable recommendations and tools. All deliverables are designed to bridge the gap between complex analytics and practical business application."
      },
      {
        question: "Can we build on the data science solutions over time?",
        answer: "Yes, our solutions are designed to be extensible and scalable. We create solid data foundations and model architectures that can be expanded as your needs evolve. Many clients start with focused projects addressing specific business challenges and gradually expand their data science capabilities across other areas of their operations."
      }
    ],
    implementationSteps: [
      {
        number: "01",
        title: "Business Problem Definition",
        description: "We work with your team to clearly define the business challenges that data science can help solve."
      },
      {
        number: "02",
        title: "Data Assessment & Preparation",
        description: "Your data is evaluated, cleaned, and structured to ensure quality inputs for analysis."
      },
      {
        number: "03",
        title: "Exploratory Analysis",
        description: "We explore your data to identify patterns, relationships, and potentially valuable insights."
      },
      {
        number: "04",
        title: "Model Development",
        description: "Statistical and machine learning models are developed based on your specific requirements."
      },
      {
        number: "05",
        title: "Validation & Refinement",
        description: "Models are rigorously tested and refined to ensure accuracy and reliability."
      },
      {
        number: "06",
        title: "Deployment & Knowledge Transfer",
        description: "We implement solutions with clear documentation and train your team on interpretation and usage."
      }
    ],
    industryStats: [
      {
        value: "21%",
        description: "average profit increase reported by companies making data-driven decisions"
      },
      {
        value: "£3.2M",
        description: "average annual benefit from implementing advanced analytics in mid-sized businesses"
      },
      {
        value: "73%",
        description: "of business leaders cite data science as critical to maintaining competitive advantage"
      },
      {
        value: "19%",
        description: "reduction in operational costs through data-optimised business processes"
      }
    ],
    potentialOutcomes: {
      title: "What We Can Do For Your Business",
      description: "Our Data Science Solutions can transform how you leverage your business data:",
      outcomes: [
        "Create predictive models that forecast business outcomes with statistical confidence",
        "Identify customer segments and behaviours that drive profitability",
        "Detect anomalies and potential issues before they impact your business",
        "Optimize pricing strategies based on market sensitivity and customer value",
        "Develop early warning systems for critical business metrics"
      ]
    }
  }
];